// Unified Wake Lock Manager
// Uses native W3C Screen Wake Lock API across modern browsers (Chrome, Safari 16.4+, Edge, Firefox 126+)

interface WakeLockSentinelLike {
  released: boolean;
  release: () => Promise<void>;
  addEventListener?: (type: string, listener: () => void) => void;
}

class WakeLockManager {
  private nativeSentinel: WakeLockSentinelLike | null = null;
  private isRequested = false;
  private acquirePromise: Promise<boolean> | null = null;
  private listenersAttached = false;

  constructor() {
    if (typeof window !== 'undefined') {
      this.attachVisibilityListeners();
    }
  }

  private updateStatusAttribute(active: boolean): void {
    if (typeof document === 'undefined') return;
    if (active) {
      document.documentElement.setAttribute('data-wake-lock', 'active');
    } else {
      document.documentElement.removeAttribute('data-wake-lock');
    }
  }

  private attachVisibilityListeners(): void {
    if (this.listenersAttached || typeof document === 'undefined') return;
    this.listenersAttached = true;

    const handleVisibilityOrFocus = async () => {
      if (document.visibilityState === 'visible') {
        if (this.isRequested && (!this.nativeSentinel || this.nativeSentinel.released)) {
          await this.acquire();
        }
      } else {
        // Document is hidden (background tab, minimized, or display lock):
        // Proactively clear attribute and release sentinel so background tabs do not prevent OS screen lock.
        this.updateStatusAttribute(false);
        if (this.nativeSentinel) {
          const sentinel = this.nativeSentinel;
          this.nativeSentinel = null;
          try {
            await sentinel.release();
          } catch {}
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('pageshow', handleVisibilityOrFocus);
  }

  private async acquireInternal(): Promise<boolean> {
    if (!this.isRequested) return false;

    // Already holding an active, unreleased sentinel
    if (this.nativeSentinel && !this.nativeSentinel.released) {
      return true;
    }

    // Do not acquire native lock if the document is currently hidden
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return false;
    }

    if (typeof navigator !== 'undefined' && 'wakeLock' in navigator) {
      try {
        const sentinel = await (navigator as any).wakeLock.request('screen');

        // Check if release() was called or the document was hidden while awaiting request('screen')
        if (!this.isRequested || (typeof document !== 'undefined' && document.visibilityState !== 'visible')) {
          try {
            await sentinel.release();
          } catch {}
          return false;
        }

        // Clean up any previously held sentinel before replacing to prevent leaks
        if (this.nativeSentinel && this.nativeSentinel !== sentinel && !this.nativeSentinel.released) {
          try {
            await this.nativeSentinel.release();
          } catch {}
        }

        this.nativeSentinel = sentinel;

        sentinel.addEventListener?.('release', () => {
          if (this.nativeSentinel === sentinel) {
            this.nativeSentinel = null;
          }
          // Only re-acquire if still requested AND the document is visible
          if (this.isRequested && typeof document !== 'undefined' && document.visibilityState === 'visible') {
            this.acquire().catch(() => {});
          } else {
            this.updateStatusAttribute(false);
          }
        });

        this.updateStatusAttribute(true);
        return true;
      } catch (error) {
        // Native wake lock can fail in low power mode or non-secure contexts
        console.warn('[WakeLock] Native wakeLock request failed:', error);
      }
    }

    return false;
  }

  public async acquire(): Promise<boolean> {
    this.isRequested = true;

    if (this.acquirePromise) {
      return this.acquirePromise;
    }

    this.acquirePromise = this.acquireInternal().finally(() => {
      this.acquirePromise = null;
    });

    return this.acquirePromise;
  }

  public async release(): Promise<void> {
    this.isRequested = false;

    if (this.nativeSentinel) {
      const sentinel = this.nativeSentinel;
      this.nativeSentinel = null;
      try {
        await sentinel.release();
      } catch {
        // Ignore release errors
      }
    }

    this.updateStatusAttribute(false);
  }

  public get isActive(): boolean {
    return this.isRequested;
  }
}

export const wakeLockManager = new WakeLockManager();

export async function requestWakeLock(): Promise<boolean> {
  return wakeLockManager.acquire();
}

export async function releaseWakeLock(): Promise<void> {
  return wakeLockManager.release();
}

export function isWakeLockActive(): boolean {
  return wakeLockManager.isActive;
}
